<template>
  <div class="login-container">
    <slot name="promotion">
      <!-- 微信小程序码 -->
      <wx-code-popover :loginIn="false" placement="left" />
    </slot>
    <div class="login-form">
      <h3 class="title">{{ systemName }}</h3>
      <el-tabs v-model="activeTab" class="login-tabs" :before-leave="beforeTabLeave">
        <!-- 微信扫码登录 -->
        <el-tab-pane label="微信扫码" name="wechat">
          <wx-scan-login
            ref="wxScan"
            scene="login"
            :count-number="countNumber"
            @code="onWxCode"
            @error="onWxError"
            @select-account="onWxSelectAccount"
            @back="onWxBack"
            @bind-send-msg="onWxBindSendMsg"
            @bind-submit="onWxBindSubmit"
          />
        </el-tab-pane>

        <!-- 验证码登录 -->
        <el-tab-pane label="验证码登录" name="sms">
          <el-form
            ref="loginForm"
            :model="loginForm"
            :rules="finalRules"
            class="inner-form"
            auto-complete="on"
            label-position="left"
            @submit.native.prevent="handleLogin"
          >
            <el-form-item prop="username">
              <span class="svg-container">
                <svg-icon icon-class="user" />
              </span>
              <el-input
                v-model.trim="loginForm.username"
                name="username"
                type="text"
                auto-complete="on"
                placeholder="请输入用户名"
              />
            </el-form-item>
            <el-form-item prop="password" v-if="loginWithPassword">
              <span class="svg-container">
                <svg-icon icon-class="password" />
              </span>
              <el-input
                :type="pwdType"
                v-model.trim="loginForm.password"
                name="password"
                auto-complete="on"
                placeholder="请输入密码"
                @keyup.enter.native="handleLogin"
                @keypress.native="keydown($event)"
              />
              <span class="show-pwd" @click="showPwd">
                <svg-icon v-if="pwdType" icon-class="eye" />
                <i v-else class="el-icon-view" />
              </span>
            </el-form-item>
            <el-form-item prop="captcha">
              <span class="svg-container">
                <svg-icon icon-class="message" />
              </span>
              <el-input
                ref="captchaInput"
                v-model.trim="loginForm.captcha"
                name="msgCode"
                placeholder="请输入短信验证码"
                maxlength="6"
                style="width: 50%"
              />
              <el-button
                :disabled="countNumber > 0"
                type="text"
                style="line-height: 45px;float:right;padding:0;padding-right:16px"
                @click="handleSendMsg"
                >{{
                  countNumber > 0 ? `重新获取(${countNumber})` : '获取短信验证码'
                }}</el-button
              >
            </el-form-item>
            <el-form-item style="border:none;">
              <el-button
                :loading="loading"
                :disabled="smsLoginDisabled"
                type="primary"
                native-type="submit"
                style="width:100%;height:45px;"
                @click.native.prevent="handleLogin"
              >
                登录
              </el-button>
            </el-form-item>
          </el-form>
        </el-tab-pane>
      </el-tabs>
    </div>
  </div>
</template>

<script>
import svgIcon from '../svg-icon'
import WxScanLogin from '@/components/WxScanLogin'

export default {
  name: 'QSLogin',
  components: { svgIcon, WxScanLogin },
  props: {
    systemName: {
      type: String,
      default: 'APP定制平台',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    countNumber: {
      type: Number,
      default: 0,
    },
    loginWithPassword: {
      type: Boolean,
      default: false,
    },
    // 微信登录已进入换登录态阶段时锁定 Tab，禁止来回切换
    tabLocked: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const validatePass = (rule, value, callback) => {
      if (value.length < 5) {
        callback(new Error('密码不能小于5位'))
      } else {
        callback()
      }
    }
    const extraRules = {
      captcha: [
        { required: true, trigger: 'blur', message: '验证码必填' },
        { pattern: /^\d{6}$/, trigger: 'blur', message: '请输入正确的验证码' },
      ],
    }
    const usernameRules = {
      username: [
        { required: true, trigger: 'blur', message: '用户名必填' },
        { pattern: /^[^\s@]+@[^\s@]+$/, trigger: 'blur', message: '请输入正确的用户名' },
      ],
    }
    const passwordRules = {
      password: [{ required: true, trigger: 'blur', validator: validatePass }],
    }
    return {
      activeTab: 'wechat',
      loginForm: {
        username: '',
        password: '',
        captcha: '',
      },
      extraRules,
      usernameRules,
      passwordRules,
      // 初始即挂载 username/captcha（及按需 password）规则，保证首次填写时失焦即可校验；
      // 之前初始值为 {}，导致按钮点击前 blur 校验完全不触发
      finalRules: {
        ...usernameRules,
        ...extraRules,
        ...(this.loginWithPassword ? passwordRules : {}),
      },
      pwdType: 'password',
    }
  },
  computed: {
    // 验证码登录 Tab 登录按钮置灰：用户名或验证码为空时不可点击
    smsLoginDisabled() {
      const { username, password, captcha } = this.loginForm
      if (!username || !captcha) return true
      if (this.loginWithPassword && !password) return true
      return false
    },
  },
  watch: {
    // 从验证码 Tab 切回微信 Tab 时重新出码（原二维码 state 可能已过期）
    activeTab(val, old) {
      if (!old || val === old) return
      if (val === 'wechat') {
        // 从验证码 Tab 切回微信：原二维码 state 可能已过期，重新出码
        this.$nextTick(() => {
          if (this.$refs.wxScan) this.$refs.wxScan.refresh()
        })
      } else if (this.$refs.wxScan) {
        // 切到验证码 Tab：丢弃本次扫码结果（select 一并丢弃），停键盘响应
        this.$refs.wxScan.resetStage()
      }
    },
  },
  methods: {
    // Tab 锁定时（微信换登录态进行中/已成功）禁止切换
    beforeTabLeave() {
      return !this.tabLocked
    },
    // 透传微信授权 code 给父级编排
    onWxCode(payload) {
      this.$emit('wxCode', payload)
    },
    onWxError(err) {
      this.$emit('wxError', err)
    },
    // 供父级重试时重新出码（如 loginKey 失效后重新扫码）
    // eslint-disable-next-line vue/no-unused-properties
    refreshWxQr() {
      if (this.$refs.wxScan) this.$refs.wxScan.refresh()
    },
    // 多账号：二维码原位置切换为账号列表（取代弹窗）
    // eslint-disable-next-line vue/no-unused-properties
    wxShowSelect(accounts) {
      if (this.$refs.wxScan) this.$refs.wxScan.showSelect(accounts)
    },
    // 未绑定：二维码原位置切换为绑定表单（取代弹窗）；reset=false 时保留已填内容
    // eslint-disable-next-line vue/no-unused-properties
    wxShowBind(reset) {
      if (this.$refs.wxScan) this.$refs.wxScan.showBind(reset)
    },
    // 换登录态进行中：二维码区显示 loading，文案由调用方传入
    // eslint-disable-next-line vue/no-unused-properties
    wxShowLoading(text) {
      if (this.$refs.wxScan) this.$refs.wxScan.showLoading(text)
    },
    // 透传：选中账号 / 返回重扫 / 绑定发码 / 绑定提交
    onWxSelectAccount(userAccount) {
      this.$emit('wxSelectAccount', userAccount)
    },
    onWxBack() {
      this.$emit('wxBack')
    },
    onWxBindSendMsg(username) {
      this.$emit('wxBindSendMsg', username)
    },
    onWxBindSubmit(payload) {
      this.$emit('wxBindSubmit', payload)
    },
    // 发送验证码（验证码登录 Tab）
    handleSendMsg() {
      if (this.loginWithPassword) {
        this.finalRules = {
          username: this.usernameRules.username,
          password: this.passwordRules.password,
        }
      } else {
        this.finalRules = {
          username: this.usernameRules.username,
        }
      }
      this.$nextTick(() => {
        this.$refs.loginForm.validate((valid) => {
          if (valid) {
            this.$emit('handleSendMsg', this.loginForm)
            this.$refs.captchaInput.focus()
          }
        })
      })
    },
    // 登录（验证码登录 Tab）
    handleLogin() {
      this.finalRules = { captcha: this.extraRules?.captcha }
      if (this.loginWithPassword) {
        this.finalRules = {
          ...this.finalRules,
          username: this.usernameRules.username,
          password: this.passwordRules.password,
        }
      } else {
        this.finalRules = {
          ...this.finalRules,
          username: this.usernameRules.username,
        }
      }
      this.$nextTick(() => {
        this.$refs.loginForm.validate((valid) => {
          if (valid) {
            this.$emit('handleLogin', this.loginForm)
          }
        })
      })
    },
    // 显示密码
    showPwd() {
      if (this.pwdType === 'password') {
        this.pwdType = ''
      } else {
        this.pwdType = 'password'
      }
    },
    keydown(e) {
      if (e.keyCode === 32) {
        e.returnValue = false
      }
    },
    // eslint-disable-next-line vue/no-unused-properties
    skipToForgetPassword() {
      this.$emit('skipToForgetPassword')
    },
  },
}
</script>

<style rel="stylesheet/scss" lang="scss">
$light_gray: #232323;

/* reset element-ui css */
.login-container {
  position: relative;
  .el-input {
    display: flex;
    height: 45px;
    flex: 1;
    input {
      background: transparent;
      border: 0px;
      -webkit-appearance: none;
      border-radius: 0px;
      padding: 12px 5px 12px 15px;
      color: $light_gray;
      height: 45px;
      box-shadow: none;
      &:-webkit-autofill {
        -webkit-box-shadow: 0 0 0px 1000px #fff inset !important;
        -webkit-text-fill-color: #232323 !important;
      }
    }
  }
  .el-form-item {
    color: #c8c9cc;
    border: 1px solid #dcdee0;
    border-radius: 2px;
    line-height: 45px;
    .el-form-item__content {
      display: flex;
    }
    &.el-form-item--small {
      margin-bottom: 24px;
    }
  }
  // el-tabs 头部不参与表单边框重置
  .login-tabs .el-tabs__header .el-form-item {
    border: none;
  }
}
</style>

<style rel="stylesheet/scss" lang="scss" scoped>
$dark_gray: #889aa4;
$light_gray: #232323;
.login-container {
  position: fixed;
  height: 100%;
  width: 100%;
  background: #212f40 url('~@/assets/img/bg_login_chewu.webp') no-repeat;
  background-size: cover;
  .login-form {
    position: absolute;
    left: auto;
    top: 50%;
    transform: translateY(-50%);
    right: 120px;
    width: 454px;
    max-width: 100%;
    padding: 37px;
    border-radius: 16px;
    background-color: #fff;
  }
  .login-tabs {
    ::v-deep .el-tabs__nav {
      float: none;
      display: flex;
      justify-content: center;
    }
    ::v-deep .el-tabs__active-bar {
      display: none;
    }

    /**
     * 微信扫码登录（二维码所在iframe是微信提供），二维码下方有一个“快捷登录”的按钮占位符
     * 在未安装微信的情况下，这个占位符不展示，就会显示大片空白，为了尽可能减少空白带来的失重感，给iframe设置高度为204px
     * 为了让微信扫码登录和验证码登录的高度一致，给el-tab-pane设置最小高度为204px，否则验证码的登录高度会比微信扫码登录的高度高
     */
    .el-tab-pane {
      min-height: 204px;
    }
  }
  .tips {
    font-size: 14px;
    color: #fff;
    margin-bottom: 10px;
    span {
      &:first-of-type {
        margin-right: 16px;
      }
    }
  }
  .svg-container {
    padding: 6px 5px 6px 15px;
    color: $dark_gray;
    vertical-align: middle;
    width: 45px;
    display: inline-block;
    background-color: #f7f8fa;
    line-height: 34px;
    border-right: 1px solid #dcdee0;
  }
  .title {
    font-size: 24px;
    color: #232323;
    font-weight: 400;
    margin: 0px auto 24px auto;
    text-align: center;
    font-weight: bold;
  }
  .show-pwd {
    position: absolute;
    right: 10px;
    top: 7px;
    font-size: 16px;
    color: $dark_gray;
    cursor: pointer;
    user-select: none;
  }
  .text-right {
    text-align: right;
  }
}

@media screen and (max-width: 602px) {
  .login-container {
    display: flex;
    justify-content: center;
    align-items: center;
    position: static;
    overflow-x: auto;
    .login-form {
      width: 320px;
      flex-shrink: 0;
      position: static;
      transform: none;
      max-width: none;
    }
  }
}
</style>
